import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Building2, MapPin, LayoutGrid, BadgeCheck, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import TierBadge from "@/components/locanest/TierBadge";

const TITLES = {
  listings: { icon: Building2, title: "Active listings", sub: "Live listings on LocaNest right now, by city and category." },
  cities: { icon: MapPin, title: "Cities live", sub: "Every city with at least one active listing." },
  categories: { icon: LayoutGrid, title: "Property categories", sub: "What kind of rentals are listed today." },
  owners: { icon: BadgeCheck, title: "Verified owners", sub: "Owners who passed Aadhaar + PAN verification." },
};

export default function StatsBreakdownDialog({ kind, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!kind) return;
    setData(null);
    api.get("/stats/breakdown").then(({ data }) => setData(data)).catch(() => setData({ cities: [], categories: [], verified_owners: [], active_listings: 0 }));
  }, [kind]);

  const meta = TITLES[kind] || TITLES.listings;
  const Icon = meta.icon;
  return (
    <Dialog open={!!kind} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="stats-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display text-2xl"><Icon size={20} className="text-[#C85A32]" /> {meta.title}</DialogTitle>
          <DialogDescription>{meta.sub}</DialogDescription>
        </DialogHeader>
        {!data ? (
          <div className="py-8 text-center text-sm text-[#5C6B7A]">Loading live data…</div>
        ) : kind === "owners" ? (
          <OwnersList owners={data.verified_owners} onClose={onClose} />
        ) : kind === "categories" ? (
          <Rows rows={data.categories.map((c) => ({ key: c.key, label: c.label, count: c.count, to: `/listings?category=${c.key}` }))} onClose={onClose} testPrefix="stat-row-cat" />
        ) : kind === "cities" ? (
          <Rows rows={data.cities.map((c) => ({ key: c.name, label: c.name, count: c.count, to: `/listings?city=${encodeURIComponent(c.name)}` }))} onClose={onClose} testPrefix="stat-row-city" />
        ) : (
          <div className="space-y-5">
            <div className="text-sm"><strong data-testid="stats-total-listings">{data.active_listings}</strong> active listings across <strong>{data.cities.length}</strong> cities.</div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C6B7A] mb-2">By city</div>
              <Rows rows={data.cities.map((c) => ({ key: c.name, label: c.name, count: c.count, to: `/listings?city=${encodeURIComponent(c.name)}` }))} onClose={onClose} testPrefix="stat-row-city" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C6B7A] mb-2">By category</div>
              <Rows rows={data.categories.map((c) => ({ key: c.key, label: c.label, count: c.count, to: `/listings?category=${c.key}` }))} onClose={onClose} testPrefix="stat-row-cat" />
            </div>
            <Link to="/listings" onClick={onClose} className="block text-center text-sm font-medium text-[#C85A32] hover:underline" data-testid="stats-view-all-listings">View all listings →</Link>
          </div>
        )}
        {data && <div className="text-[10px] text-[#5C6B7A] text-right">Live from database · {new Date(data.generated_at || Date.now()).toLocaleTimeString()}</div>}
      </DialogContent>
    </Dialog>
  );
}

function Rows({ rows, onClose, testPrefix }) {
  if (rows.length === 0) return <div className="text-sm text-[#5C6B7A]">Nothing here yet.</div>;
  const max = Math.max(...rows.map((r) => r.count), 1);
  return (
    <div className="divide-y divide-[#1A1F2C]/10 rounded-md border border-[#1A1F2C]/10 overflow-hidden">
      {rows.map((r) => (
        <Link key={r.key} to={r.to} onClick={onClose} className="flex items-center gap-3 p-3 hover:bg-[#F0EBE1] transition-colors" data-testid={`${testPrefix}-${String(r.key).toLowerCase().replace(/\s+/g, "-")}`}>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium truncate">{r.label}</div>
            <div className="h-1.5 mt-1.5 bg-[#F0EBE1] rounded-full overflow-hidden"><div className="h-full bg-[#C85A32]" style={{ width: `${(r.count / max) * 100}%` }} /></div>
          </div>
          <div className="text-sm tabular-nums font-semibold">{r.count}</div>
          <ChevronRight size={14} className="text-[#5C6B7A]" />
        </Link>
      ))}
    </div>
  );
}

function OwnersList({ owners, onClose }) {
  if (owners.length === 0) return <div className="text-sm text-[#5C6B7A]">No verified owners yet.</div>;
  return (
    <div className="space-y-2">
      {owners.map((o) => (
        <div key={o.id} className="rounded-md border border-[#1A1F2C]/10 bg-[#F0EBE1] p-3" data-testid={`stat-owner-${o.id}`}>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium">{o.name}</span>
            <TierBadge tier={o.vendor_tier || "verified"} />
            {o.rating_count > 0 && <span className="text-xs text-[#5C6B7A]">★ {o.rating_avg} ({o.rating_count})</span>}
          </div>
          {o.business_name && <div className="text-xs text-[#5C6B7A]">{o.business_name}</div>}
          <div className="text-xs mt-1">{o.listings} listing{o.listings === 1 ? "" : "s"}{o.cities.length ? ` · ${o.cities.join(", ")}` : ""} · {o.completed_deals} deals completed · since {o.since}</div>
        </div>
      ))}
      <Link to="/vendors" onClick={onClose} className="block text-center text-sm font-medium text-[#C85A32] hover:underline" data-testid="stats-view-vendors">See Top Vendors leaderboard →</Link>
    </div>
  );
}
